import React from "react";
import { motion } from "framer-motion";
import { Flame, Heart, Moon } from "lucide-react";

export default function StatsBar({ stats, onOpenStats, onSummonCelebration }) {
    if (!stats) return null;
    const pct = Math.round((stats.completion_pct || 0) * 100);
    const total = stats.tasks_total + stats.habits_total + stats.reminders_total;
    const done = stats.tasks_done + stats.habits_done + stats.reminders_done;
    const isRest = total === 0;
    const streak = stats.longest_streak || 0;
    const status = stats.streak_status || (streak > 0 ? "active" : "hibernating");
    const isHibernating = streak === 0 && status !== "active";

    // Shame-safe progress copy: "X out of Y" (never a % under 50). When user
    // has done 0, we celebrate the start — never highlight the zero.
    let progressCopy;
    if (isRest) {
        progressCopy = "Rest day 💛";
    } else if (done === 0) {
        progressCopy = "Ready when you are";
    } else if (pct >= 50) {
        progressCopy = `${pct}% — you're flowing`;
    } else {
        progressCopy = `${done} out of ${total} so far`;
    }

    return (
        <div
            className="bg-surface rounded-clay p-5 shadow-clay cursor-pointer hover:bg-surface-hover transition-colors"
            data-testid="stats-bar"
            onClick={onOpenStats}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpenStats?.(); }}
            aria-label="Open your week"
        >
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="font-body text-xs uppercase tracking-widest text-warm-500">
                        {isRest ? "Today" : "You've finished"}
                    </p>
                    {isRest ? (
                        <p className="font-display text-2xl text-warm-900 mt-0.5">Recharge day 🍃</p>
                    ) : (
                        <p className="font-display text-2xl text-warm-900 mt-0.5">
                            {done} <span className="text-warm-500 text-base">out of {total}</span>
                        </p>
                    )}
                </div>
                {isHibernating ? (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSummonCelebration?.(); }}
                        className="flex items-center gap-1.5 bg-cream rounded-full px-3 py-1.5 border border-warm-300/40 hover:bg-cream/70 transition-all active:scale-95"
                        title="Streak is resting — tap for a tiny pick-me-up"
                        data-testid="streak-pill"
                    >
                        <Moon size={14} className="text-warm-500" />
                        <span className="font-display text-sm text-warm-900" data-testid="streak-value">{streak}</span>
                        <span className="font-body text-xs text-warm-500">resting</span>
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSummonCelebration?.(); }}
                        className="flex items-center gap-1.5 bg-cream rounded-full px-3 py-1.5 border border-warm-300/40 hover:bg-cream/70 transition-all active:scale-95"
                        title="Tap for a Mochi happy moment 🍡"
                        data-testid="streak-pill"
                    >
                        <Flame size={14} className="text-terracotta" />
                        <span className="font-display text-sm text-warm-900" data-testid="streak-value">{streak}</span>
                        <span className="font-body text-xs text-warm-500">streak</span>
                    </button>
                )}
            </div>
            <div className="h-2.5 bg-cream rounded-full overflow-hidden border border-warm-300/30">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.max(pct, isRest ? 0 : 6)}%` }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className={
                        isRest
                            ? "h-full bg-warm-300/40"
                            : "h-full bg-gradient-to-r from-mustard via-terracotta to-sage"
                    }
                    data-testid="progress-fill"
                />
            </div>
            <div className="flex justify-between mt-2 text-xs font-body text-warm-500">
                <span>{progressCopy}</span>
                <span className="inline-flex items-center gap-1"><Heart size={12} /> Tap for your week</span>
            </div>
        </div>
    );
}

